import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Col, DatePicker, Form, Input, Modal, Row, Select } from 'antd';
import _ from 'lodash';
import { validateMessages } from '../../../../common/constants/common';
import { IInlineSearch } from '../../../../common/models/common';
import { ILookup } from '../../../../services/common/common.model';
import azureOnboardingService from '../../../../services/azure/azureOnboarding/azureOnboarding.service';
import {
  AzureCloudOption,
  IConnectClientManagedAzureResult,
} from '../../../../services/azure/azureOnboarding/azureOnboarding.model';
import { useAppDispatch, useAppSelector } from '../../../../store/app.hooks';
import {
  getBULookup,
  getCompanyLookup,
  getTenantLookup,
} from '../../../../store/common/common.action';
import {
  clearBULookUp,
  clearCompanyLookUp,
  commonSelector,
} from '../../../../store/common/common.reducer';
import { globalSearchSelector } from '../../../../store/globalSearch/globalSearch.reducer';
import { lookupName } from '../azureOnboarding.utils';

const { Option } = Select;

const GUID = /^[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}$/;

interface ConnectClientAppModalProps {
  onClose: () => void;
  onSaved: (result: IConnectClientManagedAzureResult, title: string) => void;
  // bu_id of every row already listed on the page - lets the form warn before it saves
  // over an existing connection.
  existingBuIds: number[];
}

const filterOption = (input: string, option: any) =>
  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
const filterSort = (optionA: any, optionB: any) =>
  optionA.children?.toLowerCase()?.localeCompare(optionB.children?.toLowerCase());

const renderOptions = (options?: ILookup[]) =>
  (options || []).map((option: ILookup) => (
    <Option key={option.id} value={option.id}>
      {option.name}
    </Option>
  ));

const ConnectClientAppModal: React.FC<ConnectClientAppModalProps> = ({
  onClose,
  onSaved,
  existingBuIds,
}) => {
  const commonLookups = useAppSelector(commonSelector);
  const globalFilters = useAppSelector(globalSearchSelector);
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();

  const [clouds, setClouds] = useState<AzureCloudOption[]>([]);
  const [cloudsLoading, setCloudsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedBuId, setSelectedBuId] = useState<number | null>(null);

  // Guards state updates that land after the modal has already been closed/unmounted
  // (e.g. a slow listClouds() or connectClientManaged() call resolving late).
  const isMounted = useRef(true);
  useEffect(
    () => () => {
      isMounted.current = false;
    },
    []
  );

  useEffect(() => {
    setCloudsLoading(true);
    azureOnboardingService
      .listClouds()
      .then((result) => {
        if (isMounted.current) setClouds(result);
      })
      .catch(() => undefined) // handled by request interceptor
      .finally(() => {
        if (isMounted.current) setCloudsLoading(false);
      });
  }, []);

  const handleTenantChange = (tenantId: number) => {
    form.setFieldsValue({ tenant_id: tenantId, company_id: null, bu_id: null });
    setSelectedBuId(null);
    if (tenantId) {
      dispatch(getCompanyLookup(tenantId));
      dispatch(clearBULookUp());
    } else {
      dispatch(clearCompanyLookUp());
      dispatch(clearBULookUp());
    }
  };

  const handleCompanyChange = (companyId: number) => {
    form.setFieldsValue({ company_id: companyId, bu_id: null });
    setSelectedBuId(null);
    if (companyId) {
      dispatch(getBULookup(companyId));
    } else {
      dispatch(clearBULookUp());
    }
  };

  const handleBuChange = (buId: number) => {
    setSelectedBuId(buId ?? null);
  };

  const hasExistingConnection = selectedBuId != null && existingBuIds.includes(selectedBuId);

  useEffect(() => {
    if (Object.keys(globalFilters?.globalTenantLookup?.data).length == 0) {
      dispatch(getTenantLookup());
    }

    const globalSearch: IInlineSearch = {};
    for (const key in globalFilters.search) {
      const element = globalFilters.search[key];
      globalSearch[key] = element ? [element] : null;
    }
    if (globalFilters.search.tenant_id && globalFilters.search.tenant_id !== 0) {
      if (!globalFilters.search.company_id) {
        dispatch(getCompanyLookup(globalSearch.tenant_id[0]));
      }
      if (!globalFilters.search.bu_id && globalFilters.search.company_id !== 0) {
        dispatch(getBULookup(globalSearch.company_id[0]));
      }
      const preselectedBuId = _.isNull(globalSearch.bu_id) ? null : globalSearch.bu_id[0];
      form.setFieldsValue({
        company_id: _.isNull(globalSearch.company_id) ? null : globalSearch.company_id[0],
        bu_id: preselectedBuId,
        tenant_id: _.isNull(globalSearch.tenant_id) ? null : globalSearch.tenant_id[0],
      });
      setSelectedBuId(preselectedBuId);
    }

    return () => {
      dispatch(clearCompanyLookUp());
      dispatch(clearBULookUp());
    };
  }, []);

  const onFinish = async (values: any) => {
    setSaving(true);
    try {
      const result = await azureOnboardingService.connectClientManaged({
        tenant_id: values.tenant_id,
        company_id: values.company_id,
        bu_id: values.bu_id,
        base_url_id: values.base_url_id,
        azure_tenant_id: values.azure_tenant_id,
        client_id: values.client_id,
        client_secret: values.client_secret?.trim() || undefined,
        client_secret_expires: values.client_secret_expires
          ? values.client_secret_expires.format('YYYY-MM-DD')
          : undefined,
      });
      const companyName = lookupName(
        values.company_id,
        commonLookups.companyLookup?.data,
        globalFilters?.globalCompanyLookup?.data
      );
      const buName = lookupName(
        values.bu_id,
        commonLookups.buLookup?.data,
        globalFilters?.globalBULookup?.data
      );
      onSaved(result, `Rediscover: ${companyName} / ${buName}`);
    } catch {
      /* handled by request interceptor */
    } finally {
      if (isMounted.current) setSaving(false);
    }
  };

  return (
    <Modal
      wrapClassName="custom-modal"
      title="Connect with client's own app"
      centered
      visible
      onCancel={onClose}
      footer={null}
      width={720}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="For clients whose Azure tenant can't consent to Synapse 360 Azure (for example Azure Government). The client registers an app in their tenant, grants it Enrollment Reader, Reservations Reader and Savings plan Reader, and gives you its IDs and secret."
      />
      <Form
        form={form}
        name="connectClientManagedAzure"
        initialValues={{ tenant_id: null, company_id: null, bu_id: null }}
        onFinish={onFinish}
        validateMessages={validateMessages}
      >
        <Row gutter={[30, 15]} className="form-label-hide">
          <Col xs={24} sm={12} md={8}>
            <div className="form-group m-0">
              Tenant
              <Form.Item
                name="tenant_id"
                className="m-0"
                label="Tenant"
                rules={[{ required: true }]}
              >
                <Select
                  onChange={handleTenantChange}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  filterOption={filterOption}
                  filterSort={filterSort}
                  loading={commonLookups.tenantLookup.loading}
                >
                  {Object.keys(globalFilters?.globalTenantLookup?.data).length > 0
                    ? renderOptions(globalFilters?.globalTenantLookup?.data)
                    : renderOptions(commonLookups.tenantLookup.data)}
                </Select>
              </Form.Item>
            </div>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <div className="form-group m-0">
              Company
              <Form.Item
                name="company_id"
                className="m-0"
                label="Company"
                rules={[{ required: true }]}
              >
                <Select
                  onChange={handleCompanyChange}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  filterOption={filterOption}
                  filterSort={filterSort}
                  loading={commonLookups.companyLookup.loading}
                >
                  {Object.keys(commonLookups.companyLookup.data).length > 0
                    ? renderOptions(commonLookups.companyLookup.data)
                    : renderOptions(globalFilters?.globalCompanyLookup?.data)}
                </Select>
              </Form.Item>
            </div>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <div className="form-group m-0">
              BU
              <Form.Item name="bu_id" className="m-0" label="BU" rules={[{ required: true }]}>
                <Select
                  onChange={handleBuChange}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  filterOption={filterOption}
                  filterSort={filterSort}
                  loading={commonLookups.buLookup.loading}
                >
                  {Object.keys(commonLookups.buLookup.data).length > 0
                    ? renderOptions(commonLookups.buLookup.data)
                    : renderOptions(globalFilters?.globalBULookup?.data)}
                </Select>
              </Form.Item>
            </div>
          </Col>
        </Row>
        {hasExistingConnection && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="This BU already has an Azure connection. Saving updates it (Verify is needed again before collection resumes)."
          />
        )}
        <Form.Item name="base_url_id" label="Azure cloud" rules={[{ required: true }]}>
          <Select placeholder="Select the client's Azure cloud" loading={cloudsLoading}>
            {clouds.map((cloud) => (
              <Option key={cloud.id} value={cloud.id}>
                {cloud.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="azure_tenant_id"
          label="Client's Azure directory (tenant) ID"
          normalize={(value) => value?.trim()}
          rules={[{ required: true, pattern: GUID, message: 'Enter the directory (tenant) ID GUID' }]}
        >
          <Input placeholder="00000000-0000-0000-0000-000000000000" autoComplete="off" />
        </Form.Item>
        <Form.Item
          name="client_id"
          label="Application (client) ID"
          normalize={(value) => value?.trim()}
          rules={[{ required: true, pattern: GUID, message: 'Enter the application (client) ID GUID' }]}
        >
          <Input placeholder="00000000-0000-0000-0000-000000000000" autoComplete="off" />
        </Form.Item>
        <Form.Item
          name="client_secret"
          label="Client secret value"
          extra="Paste the secret's Value (not its Secret ID). Leave blank to keep a secret already stored for this BU."
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>
        <Form.Item name="client_secret_expires" label="Secret expires">
          <DatePicker format="YYYY-MM-DD" />
        </Form.Item>
        <div className="btns-block modal-footer">
          <Button type="primary" htmlType="submit" loading={saving}>
            Save and rediscover
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </div>
      </Form>
    </Modal>
  );
};

export default ConnectClientAppModal;
